/**
 * All demo data for the mock Google Search Console dashboard.
 * Every number here is INVENTED for teaching — the UI carries a persistent
 * "all data invented" badge.
 */

/* Series colors — color follows the entity everywhere (charts, legends,
 * stat-tile accents, table header dots). Validated for CVD on #0b0e1a. */
export const CLICKS_COLOR = "#0891b2";
export const IMPRESSIONS_COLOR = "#6366f1";
/** Distinct entity (indexed pages) → its own hue: the chapter accent. */
export const SPARKLINE_COLOR = "#a78bfa";

/* Status colors (Core Web Vitals) — always paired with icon + text label. */
export const STATUS_GOOD = "#34d399";
export const STATUS_NEEDS = "#fbbf24";
export const STATUS_POOR = "#fb7185";

export const SURFACE = "#0b0e1a";

/* ---------------------------------------------------------------- */
/* Performance                                                       */
/* ---------------------------------------------------------------- */

export interface PerfWeek {
  week: string;
  clicks: number;
  impressions: number;
}

/** 16 weekly points, Mar→Jun — gentle steady growth, no seasonal spike. */
export const PERF_WEEKS: PerfWeek[] = [
  { week: "Mar 2", clicks: 330, impressions: 22800 },
  { week: "Mar 9", clicks: 340, impressions: 23200 },
  { week: "Mar 16", clicks: 335, impressions: 23000 },
  { week: "Mar 23", clicks: 355, impressions: 23900 },
  { week: "Mar 30", clicks: 360, impressions: 24200 },
  { week: "Apr 6", clicks: 375, impressions: 24800 },
  { week: "Apr 13", clicks: 370, impressions: 24500 },
  { week: "Apr 20", clicks: 390, impressions: 25300 },
  { week: "Apr 27", clicks: 400, impressions: 25600 },
  { week: "May 4", clicks: 415, impressions: 26100 },
  { week: "May 11", clicks: 425, impressions: 26400 },
  { week: "May 18", clicks: 440, impressions: 26900 },
  { week: "May 25", clicks: 450, impressions: 27300 },
  { week: "Jun 1", clicks: 465, impressions: 27800 },
  { week: "Jun 8", clicks: 470, impressions: 28000 },
  { week: "Jun 15", clicks: 480, impressions: 28400 },
];

export interface QueryRow {
  query: string;
  clicks: number;
  impressions: number;
  ctr: string;
  position: number;
  /** High impressions + weak average position → a content gap. */
  gap?: boolean;
}

export const QUERY_ROWS: QueryRow[] = [
  { query: "stability running shoes", clicks: 620, impressions: 18400, ctr: "3.4%", position: 8.6 },
  {
    query: "best running shoes for flat feet",
    clicks: 210,
    impressions: 22600,
    ctr: "0.9%",
    position: 11.2,
    gap: true,
  },
  {
    query: "running shoes for flat feet weeknight training",
    clicks: 140,
    impressions: 1900,
    ctr: "7.4%",
    position: 4.2,
  },
  {
    query: "how to choose running shoes",
    clicks: 160,
    impressions: 19800,
    ctr: "0.8%",
    position: 14.1,
    gap: true,
  },
  { query: "running shoe fitting guide", clicks: 310, impressions: 6200, ctr: "5.0%", position: 5.8 },
  {
    query: "arch support inserts vs stability shoes",
    clicks: 95,
    impressions: 2400,
    ctr: "4.0%",
    position: 6.9,
  },
];

/* ---------------------------------------------------------------- */
/* Indexing                                                          */
/* ---------------------------------------------------------------- */

export const INDEXING = { indexed: 8420, notIndexed: 2910 };

export interface IndexBar {
  name: string;
  value: number;
  label: string;
}

export const INDEX_COMPARE: IndexBar[] = [
  { name: "Indexed", value: 8420, label: "8,420" },
  { name: "Not indexed", value: 2910, label: "2,910" },
];

export interface IndexReason {
  reason: string;
  pages: number;
}

/** The four reasons worth fixing on important templates FIRST. */
export const INDEX_REASONS: IndexReason[] = [
  { reason: "Not found (404) · 404 chains", pages: 1240 },
  { reason: "Duplicate without user-selected canonical", pages: 890 },
  { reason: "Excluded by 'noindex' tag", pages: 470 },
  { reason: "Soft 404", pages: 310 },
];

export interface SparkPoint {
  week: string;
  indexed: number;
}

/** Gentle rise with ONE visible dip mid-series (May 11). */
export const INDEXED_TREND: SparkPoint[] = [
  { week: "Mar 30", indexed: 7600 },
  { week: "Apr 6", indexed: 7700 },
  { week: "Apr 13", indexed: 7780 },
  { week: "Apr 20", indexed: 7860 },
  { week: "Apr 27", indexed: 7940 },
  { week: "May 4", indexed: 8020 },
  { week: "May 11", indexed: 7350 },
  { week: "May 18", indexed: 7980 },
  { week: "May 25", indexed: 8120 },
  { week: "Jun 1", indexed: 8240 },
  { week: "Jun 8", indexed: 8330 },
  { week: "Jun 15", indexed: 8420 },
];

/* ---------------------------------------------------------------- */
/* Core Web Vitals                                                   */
/* ---------------------------------------------------------------- */

export interface VitalsRow {
  device: string;
  good: number;
  needsImprovement: number;
  poor: number;
}

export const CWV_MOBILE: VitalsRow = {
  device: "Mobile",
  good: 5200,
  needsImprovement: 1700,
  poor: 800,
};

export const CWV_DESKTOP: VitalsRow = {
  device: "Desktop",
  good: 7100,
  needsImprovement: 480,
  poor: 120,
};

/* ---------------------------------------------------------------- */
/* Sitemaps                                                          */
/* ---------------------------------------------------------------- */

export interface SitemapRow {
  file: string;
  discovered: number;
  lastRead: string;
}

export const SITEMAPS: SitemapRow[] = [
  { file: "sitemap-guides.xml", discovered: 240, lastRead: "2 days ago" },
  { file: "sitemap-products.xml", discovered: 7900, lastRead: "2 days ago" },
  { file: "sitemap-categories.xml", discovered: 380, lastRead: "6 days ago" },
];

/* ---------------------------------------------------------------- */
/* Teaching callouts (Hotspot → ExplainerPanel)                      */
/* ---------------------------------------------------------------- */

export interface Callout {
  id: string;
  n: number;
  eyebrow: string;
  title: string;
  body: string;
}

export const PERF_CALLOUTS = {
  gaps: {
    id: "perf-gaps",
    n: 1,
    eyebrow: "Performance · 1 of 3",
    title: "Impressions + weak position = content gap",
    body: "Filter Queries for phrases with impressions but a WEAK average position. 'best running shoes for flat feet' gets 22,600 impressions at position 11 — the demand exists, your page just isn't the answer yet. These rows are your content gaps.",
  },
  pages: {
    id: "perf-pages",
    n: 2,
    eyebrow: "Performance · 2 of 3",
    title: "Pages earn unexpected queries",
    body: "Compare Pages to see which URLs earn UNEXPECTED queries — the fitting guide picks up 'arch support inserts vs stability shoes' without ever targeting it. Consider expanding those topics into deeper sections or pages.",
  },
  compare: {
    id: "perf-compare",
    n: 3,
    eyebrow: "Performance · 3 of 3",
    title: "Date range + compare",
    body: "The date range and 'compare' mode show the effect after a change or a launch. Use the data — not gut feel — to decide the next page or section to improve.",
  },
} satisfies Record<string, Callout>;

export const INDEX_CALLOUTS = {
  templates: {
    id: "index-templates",
    n: 1,
    eyebrow: "Indexing · 1 of 3",
    title: "Fix templates before chasing scores",
    body: "404 chains, soft 404s, and an accidental 'noindex' on an important template — fix these FIRST, before chasing perfect scores. One bad template setting can pull entire sections out of the index.",
  },
  dip: {
    id: "index-dip",
    n: 2,
    eyebrow: "Indexing · 2 of 3",
    title: "The fire alarm",
    body: "A sudden drop in indexed pages is the fire alarm of this report — the day you stop and investigate what shipped.",
  },
  reasons: {
    id: "index-reasons",
    n: 3,
    eyebrow: "Indexing · 3 of 3",
    title: "Judge the reasons, not the total",
    body: "Not indexed isn't automatically bad — filtered variants and true duplicates SHOULD be excluded. Judge the reasons, not the total.",
  },
} satisfies Record<string, Callout>;

export const CWV_CALLOUTS = {
  mobile: {
    id: "cwv-mobile",
    n: 1,
    eyebrow: "Core Web Vitals · 1 of 3",
    title: "Mobile is what Google indexes",
    body: "Mobile is the report that matters — Google predominantly indexes the mobile version. If mobile fails, desktop speed won't save you.",
  },
  template: {
    id: "cwv-template",
    n: 2,
    eyebrow: "Core Web Vitals · 2 of 3",
    title: "One fix, thousands of URLs",
    body: "Pages fail in TEMPLATE groups, not one by one. Fix the article template once and every URL it renders improves — one dev ticket, not 1,700.",
  },
  legend: {
    id: "cwv-legend",
    n: 3,
    eyebrow: "Core Web Vitals · 3 of 3",
    title: "The three vitals",
    body: "LCP = loads fast. INP = responds fast when tapped. CLS = doesn't jump around while loading.",
  },
} satisfies Record<string, Callout>;

export const SITEMAP_CALLOUTS = {
  menu: {
    id: "sitemap-menu",
    n: 1,
    eyebrow: "Sitemaps · 1 of 2",
    title: "The menu you hand Google",
    body: "A sitemap is the menu you hand Google: 'here's everything worth indexing'. Splitting by type — guides, products, categories — shows you instantly WHERE a problem lives.",
  },
  lastRead: {
    id: "sitemap-last-read",
    n: 2,
    eyebrow: "Sitemaps · 2 of 2",
    title: "Check the 'last read' date",
    body: "Check the 'last read' date. If Google hasn't read your sitemap in weeks, your newest pages are invisible to it.",
  },
} satisfies Record<string, Callout>;
